import { ChevronRightIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { AnimatePresence, ChevronRotate, ExpandContent } from './Animations';
import { cn } from './cn';

export interface ExpanderProps {
  title: React.ReactNode;
  /** Optional short hint rendered right-aligned in the header row. */
  hint?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
  dataTest?: string;
}

/**
 * Disclosure row for secondary ("advanced") content — collapsed by default so the
 * primary content stays scannable. Header is a real button (keyboard + SR friendly).
 */
export const Expander = ({ title, hint, defaultOpen = false, children, className, dataTest }: ExpanderProps) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const contentId = React.useId();
  return (
    <div className={cn('rounded-lg border border-border-subtle bg-surface', className)} data-test={dataTest}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-4 py-3 text-left',
          'type-label text-text-secondary transition-colors duration-200 hover:bg-surface-hover hover:text-text-primary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-focus-ring-offset'
        )}
      >
        <ChevronRotate open={open} className="flex items-center">
          <ChevronRightIcon className="h-4 w-4" />
        </ChevronRotate>
        <span className="grow">{title}</span>
        {hint && <span className="type-caption text-text-muted">{hint}</span>}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <ExpandContent id={contentId} className="overflow-hidden">
            <div className="px-4 pb-4 pt-1">{children}</div>
          </ExpandContent>
        )}
      </AnimatePresence>
    </div>
  );
};
